// src/store/slices/main/groupSlice.js - UPDATED with correct endpoints

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

// =============================================
// GROUP THUNKS
// =============================================

export const getGroups = createAsyncThunk(
    'groups/getGroups',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.GROUPS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Groups retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch groups',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get groups error:', error);
            let errorMessage = 'Failed to fetch groups';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch groups';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getGroup = createAsyncThunk(
    'groups/getGroup',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.GROUP(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group retrieved successfully',
                    group: data?.group || null,
                    members: data?.members || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get group error:', error);
            let errorMessage = 'Failed to fetch group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch group';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createGroup = createAsyncThunk(
    'groups/createGroup',
    async (groupData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.CREATE_GROUP, groupData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Group created successfully',
                    group: data?.group || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create group error:', error);
            let errorMessage = 'Failed to create group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to create group';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateGroup = createAsyncThunk(
    'groups/updateGroup',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.UPDATE_GROUP(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group updated successfully',
                    group: responseData?.group || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update group error:', error);
            let errorMessage = 'Failed to update group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to update group';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteGroup = createAsyncThunk(
    'groups/deleteGroup',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_GROUP(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete group error:', error);
            let errorMessage = 'Failed to delete group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to delete group';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const toggleGroupStatus = createAsyncThunk(
    'groups/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.GROUP_TOGGLE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group status toggled successfully',
                    id: id,
                    is_active: data?.is_active || false
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to toggle group status',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Toggle group status error:', error);
            let errorMessage = 'Failed to toggle group status';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to toggle group status';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const duplicateGroup = createAsyncThunk(
    'groups/duplicate',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.GROUP_DUPLICATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group duplicated successfully',
                    group: data?.group || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to duplicate group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Duplicate group error:', error);
            let errorMessage = 'Failed to duplicate group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to duplicate group';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getGroupStats = createAsyncThunk(
    'groups/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.GROUPS_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch group statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get group stats error:', error);
            let errorMessage = 'Failed to fetch group statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch group statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportGroups = createAsyncThunk(
    'groups/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.GROUPS_EXPORT, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Groups exported successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export groups',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Export groups error:', error);
            let errorMessage = 'Failed to export groups';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to export groups';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// GROUP MEMBER THUNKS - UPDATED TO USE CORRECT ROUTES
// =============================================

/**
 * Get all members of a group with filters and pagination
 * Route: GET /{groupId}/members
 */
export const getGroupMembers = createAsyncThunk(
    'groups/getGroupMembers',
    async ({ groupId, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching group members for group:', groupId, 'with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.GROUP_MEMBERS(groupId), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group members retrieved successfully',
                    data: data || {},
                    groupId: groupId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch group members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get group members error:', error);
            let errorMessage = 'Failed to fetch group members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch group members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get group member statistics
 * Route: GET /{groupId}/members/stats
 */
export const getGroupMemberStats = createAsyncThunk(
    'groups/getMemberStats',
    async (groupId, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching group member stats for group:', groupId);

            const response = await apiClient.get(API_ENDPOINTS.GROUP_MEMBER_STATS(groupId));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group member statistics retrieved successfully',
                    stats: data || {},
                    groupId: groupId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch group member statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get group member stats error:', error);
            let errorMessage = 'Failed to fetch group member statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch group member statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Add a single member to a group
 * Route: POST /{groupId}/members/create
 */
export const addMemberToGroup = createAsyncThunk(
    'groups/addMember',
    async ({ groupId, memberData }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Adding member to group:', groupId, 'with data:', memberData);

            const response = await apiClient.post(API_ENDPOINTS.GROUP_MEMBER_CREATE(groupId), memberData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Member added to group successfully',
                    membership: data?.membership || data || null,
                    groupId: groupId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to add member to group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Add member to group error:', error);
            let errorMessage = 'Failed to add member to group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to add member to group';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Bulk add members to a group
 * Route: POST /{groupId}/members/bulk
 */
export const addMembersToGroup = createAsyncThunk(
    'groups/addMembers',
    async ({ groupId, membersData }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Bulk adding members to group:', groupId, 'with data:', membersData);

            const response = await apiClient.post(API_ENDPOINTS.GROUP_MEMBERS_BULK(groupId), membersData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Members added to group successfully',
                    data: data || {},
                    groupId: groupId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to add members to group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Bulk add members to group error:', error);
            let errorMessage = 'Failed to add members to group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to add members to group';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get a single group member
 * Route: GET /{groupId}/members/{memberId}
 */
export const getGroupMember = createAsyncThunk(
    'groups/getGroupMember',
    async ({ groupId, memberId }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching group member:', memberId, 'from group:', groupId);

            const response = await apiClient.get(API_ENDPOINTS.GROUP_MEMBER(groupId, memberId));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group member retrieved successfully',
                    member: data || null,
                    groupId: groupId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch group member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get group member error:', error);
            let errorMessage = 'Failed to fetch group member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch group member';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Update a group member
 * Route: PUT /{groupId}/members/update/{memberId}
 */
export const updateGroupMember = createAsyncThunk(
    'groups/updateGroupMember',
    async ({ groupId, memberId, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating group member:', memberId, 'in group:', groupId);
            console.log('Update data:', data);

            const response = await apiClient.put(API_ENDPOINTS.GROUP_MEMBER_UPDATE(groupId, memberId), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Group member updated successfully',
                    member: responseData || null,
                    groupId: groupId,
                    memberId: memberId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update group member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update group member error:', error);
            let errorMessage = 'Failed to update group member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to update group member';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Delete/Remove a group member
 * Route: DELETE /{groupId}/members/delete/{memberId}
 */
export const removeMemberFromGroup = createAsyncThunk(
    'groups/removeMember',
    async ({ groupId, memberId }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Removing member:', memberId, 'from group:', groupId);

            const response = await apiClient.delete(API_ENDPOINTS.GROUP_MEMBER_DELETE(groupId, memberId));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member removed from group successfully',
                    groupId: groupId,
                    memberId: memberId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to remove member from group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Remove member from group error:', error);
            let errorMessage = 'Failed to remove member from group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to remove member from group';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// Keep the old alias for backward compatibility
export const updateGroupMemberStatus = updateGroupMember;

/**
 * Send SMS to group
 * Route: POST /groups/{id}/sms
 */
export const sendSmsToGroup = createAsyncThunk(
    'groups/sendSms',
    async ({ id, message, sender_id = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.GROUP_SEND_SMS(id), {
                message,
                sender_id
            });
            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'SMS sent to group successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: responseMessage || 'Failed to send SMS to group',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Send SMS to group error:', error);
            let errorMessage = 'Failed to send SMS to group';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to send SMS to group';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// SLICE - Keep the same as before
// =============================================

const initialState = {
    groups: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentGroup: null,
    groupMembers: [],
    groupMembersPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    groupMemberStats: null,
    stats: null,
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const groupSlice = createSlice({
    name: 'groups',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearGroupState: (state) => {
            state.groups = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentGroup = null;
            state.groupMembers = [];
            state.groupMembersPagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.groupMemberStats = null;
            state.stats = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentGroup: (state) => {
            state.currentGroup = null;
            state.groupMembers = [];
            state.groupMemberStats = null;
        },
        clearGroupMembers: (state) => {
            state.groupMembers = [];
            state.groupMembersPagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET GROUPS =====
            .addCase(getGroups.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getGroups.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.groups = responseData.data || [];
                state.pagination = {
                    current_page: responseData.meta?.current_page || 1,
                    last_page: responseData.meta?.last_page || 1,
                    per_page: responseData.meta?.per_page || 15,
                    total: responseData.meta?.total || 0
                };
                state.stats = responseData.meta?.stats || null;
                state.successMessage = payload.message || 'Groups retrieved successfully';
                state.apiMessage = payload.message || 'Groups retrieved successfully';
                state.error = null;
            })
            .addCase(getGroups.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch groups';
                state.apiMessage = action.payload?.message || 'Failed to fetch groups';
                state.successMessage = null;
                state.groups = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET GROUP =====
            .addCase(getGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentGroup = payload.group || null;
                state.groupMembers = payload.members?.data || [];
                state.groupMembersPagination = payload.members?.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Group retrieved successfully';
                state.apiMessage = payload.message || 'Group retrieved successfully';
                state.error = null;
            })
            .addCase(getGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch group';
                state.apiMessage = action.payload?.message || 'Failed to fetch group';
                state.successMessage = null;
                state.currentGroup = null;
                state.groupMembers = [];
            })

            // ===== GET GROUP MEMBERS =====
            .addCase(getGroupMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getGroupMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.groupMembers = responseData.data || [];
                state.groupMembersPagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Group members retrieved successfully';
                state.apiMessage = payload.message || 'Group members retrieved successfully';
                state.error = null;
            })
            .addCase(getGroupMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch group members';
                state.apiMessage = action.payload?.message || 'Failed to fetch group members';
                state.successMessage = null;
                state.groupMembers = [];
                state.groupMembersPagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET GROUP MEMBER STATS =====
            .addCase(getGroupMemberStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getGroupMemberStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.groupMemberStats = payload.stats || null;
                state.successMessage = payload.message || 'Group member statistics retrieved successfully';
                state.apiMessage = payload.message || 'Group member statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getGroupMemberStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch group member statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch group member statistics';
                state.successMessage = null;
                state.groupMemberStats = null;
            })

            // ===== ADD MEMBER TO GROUP =====
            .addCase(addMemberToGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(addMemberToGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member added to group successfully';
                state.apiMessage = payload.message || 'Member added to group successfully';
                state.error = null;
            })
            .addCase(addMemberToGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to add member to group';
                state.apiMessage = action.payload?.message || 'Failed to add member to group';
                state.successMessage = null;
            })

            // ===== ADD MEMBERS TO GROUP (BULK) =====
            .addCase(addMembersToGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(addMembersToGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Members added to group successfully';
                state.apiMessage = payload.message || 'Members added to group successfully';
                state.error = null;
            })
            .addCase(addMembersToGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to add members to group';
                state.apiMessage = action.payload?.message || 'Failed to add members to group';
                state.successMessage = null;
            })

            // ===== GET SINGLE GROUP MEMBER =====
            .addCase(getGroupMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getGroupMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                // Store the current member in a temporary location or handle as needed
                state.successMessage = payload.message || 'Group member retrieved successfully';
                state.apiMessage = payload.message || 'Group member retrieved successfully';
                state.error = null;
            })
            .addCase(getGroupMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch group member';
                state.apiMessage = action.payload?.message || 'Failed to fetch group member';
                state.successMessage = null;
            })

            // ===== UPDATE GROUP MEMBER =====
            .addCase(updateGroupMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateGroupMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Group member updated successfully';
                state.apiMessage = payload.message || 'Group member updated successfully';
                state.error = null;
                // Update the member in the list if present
                if (payload.memberId && state.groupMembers) {
                    state.groupMembers = state.groupMembers.map(m => 
                        m.id === payload.memberId ? { ...m, ...payload.member } : m
                    );
                }
            })
            .addCase(updateGroupMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update group member';
                state.apiMessage = action.payload?.message || 'Failed to update group member';
                state.successMessage = null;
            })

            // ===== REMOVE MEMBER FROM GROUP =====
            .addCase(removeMemberFromGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(removeMemberFromGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member removed from group successfully';
                state.apiMessage = payload.message || 'Member removed from group successfully';
                state.error = null;
                // Remove the member from the list
                if (payload.memberId && state.groupMembers) {
                    state.groupMembers = state.groupMembers.filter(m => m.id !== payload.memberId);
                }
            })
            .addCase(removeMemberFromGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to remove member from group';
                state.apiMessage = action.payload?.message || 'Failed to remove member from group';
                state.successMessage = null;
            })

            // ===== CREATE GROUP =====
            .addCase(createGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Group created successfully';
                state.apiMessage = payload.message || 'Group created successfully';
                state.error = null;
                if (payload.group) {
                    state.groups = [payload.group, ...state.groups];
                }
            })
            .addCase(createGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create group';
                state.apiMessage = action.payload?.message || 'Failed to create group';
                state.successMessage = null;
            })

            // ===== UPDATE GROUP =====
            .addCase(updateGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Group updated successfully';
                state.apiMessage = payload.message || 'Group updated successfully';
                state.error = null;
                if (payload.group) {
                    state.groups = state.groups.map(g => 
                        g.id === payload.group.id ? payload.group : g
                    );
                    if (state.currentGroup?.id === payload.group.id) {
                        state.currentGroup = payload.group;
                    }
                }
            })
            .addCase(updateGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update group';
                state.apiMessage = action.payload?.message || 'Failed to update group';
                state.successMessage = null;
            })

            // ===== DELETE GROUP =====
            .addCase(deleteGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Group deleted successfully';
                state.apiMessage = payload.message || 'Group deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.groups = state.groups.filter(g => g.id !== payload.id);
                    if (state.currentGroup?.id === payload.id) {
                        state.currentGroup = null;
                        state.groupMembers = [];
                    }
                }
            })
            .addCase(deleteGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete group';
                state.apiMessage = action.payload?.message || 'Failed to delete group';
                state.successMessage = null;
            })

            // ===== TOGGLE GROUP STATUS =====
            .addCase(toggleGroupStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleGroupStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Group status toggled';
                state.apiMessage = payload.message || 'Group status toggled';
                state.error = null;
                if (payload.id && payload.is_active !== undefined) {
                    state.groups = state.groups.map(g => 
                        g.id === payload.id ? { ...g, is_active: payload.is_active } : g
                    );
                    if (state.currentGroup?.id === payload.id) {
                        state.currentGroup = { ...state.currentGroup, is_active: payload.is_active };
                    }
                }
            })
            .addCase(toggleGroupStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle group status';
                state.apiMessage = action.payload?.message || 'Failed to toggle group status';
                state.successMessage = null;
            })

            // ===== DUPLICATE GROUP =====
            .addCase(duplicateGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(duplicateGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Group duplicated successfully';
                state.apiMessage = payload.message || 'Group duplicated successfully';
                state.error = null;
                if (payload.group) {
                    state.groups = [payload.group, ...state.groups];
                }
            })
            .addCase(duplicateGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate group';
                state.apiMessage = action.payload?.message || 'Failed to duplicate group';
                state.successMessage = null;
            })

            // ===== GET GROUP STATS =====
            .addCase(getGroupStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getGroupStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = payload.stats || null;
                state.successMessage = payload.message || 'Group statistics retrieved';
                state.apiMessage = payload.message || 'Group statistics retrieved';
                state.error = null;
            })
            .addCase(getGroupStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch group statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch group statistics';
                state.successMessage = null;
                state.stats = null;
            })

            // ===== EXPORT GROUPS =====
            .addCase(exportGroups.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportGroups.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Groups exported successfully';
                state.apiMessage = payload.message || 'Groups exported successfully';
                state.error = null;
            })
            .addCase(exportGroups.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export groups';
                state.apiMessage = action.payload?.message || 'Failed to export groups';
                state.successMessage = null;
            })

            // ===== SEND SMS TO GROUP =====
            .addCase(sendSmsToGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendSmsToGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'SMS sent to group';
                state.apiMessage = payload.message || 'SMS sent to group';
                state.error = null;
            })
            .addCase(sendSmsToGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send SMS to group';
                state.apiMessage = action.payload?.message || 'Failed to send SMS to group';
                state.successMessage = null;
            });
    },
});

// =============================================
// EXPORT ACTIONS
// =============================================
export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearGroupState,
    clearCurrentGroup,
    clearGroupMembers,
} = groupSlice.actions;

// =============================================
// EXPORT SELECTORS
// =============================================
export const selectGroups = (state) => state.groups?.groups || [];
export const selectGroupPagination = (state) => state.groups?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentGroup = (state) => state.groups?.currentGroup || null;
export const selectGroupMembers = (state) => state.groups?.groupMembers || [];
export const selectGroupMembersPagination = (state) => state.groups?.groupMembersPagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectGroupMemberStats = (state) => state.groups?.groupMemberStats || null;
export const selectGroupStats = (state) => state.groups?.stats || null;
export const selectGroupsLoading = (state) => state.groups?.isLoading || false;
export const selectGroupsError = (state) => state.groups?.error || null;
export const selectGroupsSuccess = (state) => state.groups?.successMessage || null;
export const selectGroupsApiMessage = (state) => state.groups?.apiMessage || null;

// Summary selectors
export const selectGroupStatsSummary = (state) => {
    const stats = selectGroupStats(state);
    if (!stats) return null;
    return {
        total: stats.total || 0,
        active: stats.active || 0,
        inactive: stats.inactive || 0,
        total_members: stats.total_members || 0,
        largest_group: stats.largest_group || null
    };
};

export const selectGroupMemberStatsSummary = (state) => {
    const stats = selectGroupMemberStats(state);
    if (!stats) return null;
    return {
        total: stats.total || 0,
        active: stats.active || 0,
        inactive: stats.inactive || 0,
        pending: stats.pending || 0,
        recent_joined: stats.recent_joined || 0
    };
};

export default groupSlice.reducer;